import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tabs,
  Tab,
  Box,
  Alert
} from '@mui/material';
import timeEntryService from '../services/timeEntryService';
import taskEntryService from '../services/taskEntryService';
import errorEntryService from '../services/errorEntryService';
import authService from '../services/authService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `report-tab-${index}`,
    'aria-controls': `report-tabpanel-${index}`,
  };
}

interface DailySummary {
  time_entries: any[];
  task_entries: any[];
  error_entries: any[];
}

interface WeeklyAverage {
  time_entries: any[];
  task_entries: any[];
  error_entries: any[];
}

interface MonthlyErrorCount {
  error_count: { [key: string]: number };
}

const Reports: React.FC = () => {
  const [dailySummary, setDailySummary] = useState<DailySummary | null>(null);
  const [weeklyAverage, setWeeklyAverage] = useState<WeeklyAverage | null>(null);
  const [monthlyErrorCount, setMonthlyErrorCount] = useState<MonthlyErrorCount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const currentUserRole = authService.getCurrentUser()?.role || '';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    fetchDailySummary();
  }, []);

  const fetchDailySummary = async () => {
    try {
      setLoading(true);
      const timeEntries = await timeEntryService.getAll();
      const taskEntries = await taskEntryService.getAll();
      const errorEntries = await errorEntryService.getAll();
      
      setDailySummary({
        time_entries: timeEntries,
        task_entries: taskEntries,
        error_entries: errorEntries
      });
      setError(null);
    } catch (err) {
      setError('Failed to fetch daily summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchWeeklyAverage = async () => {
    try {
      // In a real application, you would fetch weekly average data
      // For now, we'll use mock data
      const mockWeeklyAverage: WeeklyAverage = {
        time_entries: [],
        task_entries: [],
        error_entries: []
      };
      
      setWeeklyAverage(mockWeeklyAverage);
      setError(null);
    } catch (err) {
      setError('Failed to fetch weekly average');
    }
  };

  const fetchMonthlyErrorCount = async () => {
    try {
      // In a real application, you would fetch monthly error count data
      // For now, we'll use mock data
      const mockMonthlyErrorCount: MonthlyErrorCount = {
        error_count: {
          '1': 2,
          '2': 1
        }
      };
      
      setMonthlyErrorCount(mockMonthlyErrorCount);
      setError(null);
    } catch (err) {
      setError('Failed to fetch monthly error count');
    }
  };

  const handleTabChangeWithFetch = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    switch (newValue) {
      case 0:
        fetchDailySummary();
        break;
      case 1:
        fetchWeeklyAverage();
        break;
      case 2:
        fetchMonthlyErrorCount();
        break;
    }
  };

  if (loading && tabValue === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  // Only Admin and Manager can view reports
  if (currentUserRole !== 'Admin' && currentUserRole !== 'Manager') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Alert severity="error">Access denied. Only administrators and managers can view reports.</Alert>
      </div>
    );
  }

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Reports
      </Typography>

      <Tabs value={tabValue} onChange={handleTabChangeWithFetch} aria-label="report tabs">
        <Tab label="Daily Summary" {...a11yProps(0)} />
        <Tab label="Weekly Average" {...a11yProps(1)} />
        <Tab label="Monthly Error Count" {...a11yProps(2)} />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card>
            <CardHeader title="Time Entries" />
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    {currentUserRole === 'Admin' && <TableCell>User ID</TableCell>}
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Break Time (min)</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dailySummary?.time_entries.map((entry) => (
                    <TableRow key={entry.id}>
                      {currentUserRole === 'Admin' && <TableCell>{entry.user_id}</TableCell>}
                      <TableCell>{new Date(entry.start_time).toLocaleString()}</TableCell>
                      <TableCell>{new Date(entry.end_time).toLocaleString()}</TableCell>
                      <TableCell>{entry.break_time}</TableCell>
                      <TableCell>{entry.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Task Entries" />
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    {currentUserRole === 'Admin' && <TableCell>User ID</TableCell>}
                    {currentUserRole === 'Admin' && <TableCell>Department</TableCell>}
                    <TableCell>Task Type</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Time Spent (min)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dailySummary?.task_entries.map((entry) => (
                    <TableRow key={entry.id}>
                      {currentUserRole === 'Admin' && <TableCell>{entry.user_id}</TableCell>}
                      {currentUserRole === 'Admin' && <TableCell>{entry.department}</TableCell>}
                      <TableCell>{entry.task_type}</TableCell>
                      <TableCell>{entry.quantity}</TableCell>
                      <TableCell>{entry.time_spent}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {currentUserRole === 'Admin' && (
            <Card>
              <CardHeader title="Error Entries" />
              <CardContent>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Error Type</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dailySummary?.error_entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{entry.user_id}</TableCell>
                        <TableCell>{entry.department}</TableCell>
                        <TableCell>{entry.error_type}</TableCell>
                        <TableCell>{entry.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Alert severity="info">Weekly Average Report - Implementation pending</Alert>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Alert severity="info">Monthly Error Count Report - Implementation pending</Alert>
      </TabPanel>
    </div>
  );
};

export default Reports;